import { prefetchPathname } from "gatsby"
import React, { useEffect, useState, useRef } from "react"
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md"
import * as styles from "./DesktopMenu.module.scss"
import { Link, navigate } from "gatsby"
import { useLink, useSiteQuery } from "../../../../hooks/index"
import { useHeaderContext } from "../../../../hooks/layout/HeaderContext"
import { useSiteQueryThree } from "../../../../hooks/layout/useSiteQueryThree"
import debounce from "lodash.debounce"
import { useCallback } from "react"
import HeaderImageSlider from "../../../HeaderImageSlider/HeaderImageSlider"
import { useSiteQueryFour } from "../../../../hooks/layout/useSiteQueryFour"
import WhatsAppLink from "../../../WhatsApp/WhatsAppLink"
import { useSaaS } from "../../../../context/SaaSContext"
import { LogOut } from "lucide-react"

const LayoutDashboard = ({
  size = 18,
  className = "",
  ...props
}: {
  size?: number
  className?: string
  [key: string]: any
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <line x1="14" y1="15" x2="21" y2="15" />
    <line x1="14" y1="19" x2="19" y2="19" />
  </svg>
)

const DesktopMenu = ({ menuItems }) => {
  const {
    quoteSection,
    headerBlogTitle,
    headerButton,
    headerBlogMedia,
    quoteSectionTitle,
    headerLogo,
  } = useSiteQuery()

  const { search } = useSiteQueryFour()
  const megaMenuRef = useRef(null)

  const {
    openDropdown,
    setOpenDropdown,
    childOpenState,
    setChildOpenState,
    subChildOpenState,
    setSubChildOpenState,
    handleLinkClick,
  } = useHeaderContext()

  const { headerText, getAQuoteButton, allServices, loginText } =
    useSiteQueryThree()

  const { allResources } = useSiteQueryFour()
  const { user, openAuthModal, logout } = useSaaS()
  const dashboardRedirectPath = useLink("dashboard")

  const [searchTerm, setSearchTerm] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false)
  const profileDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target as Node)
      ) {
        setProfileDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (!user) {
      setProfileDropdownOpen(false)
    }
  }, [user])

  const findId = title => {
    if (!title || typeof title !== "string") return ""
    return title.split(" ").join("-").toLowerCase()
  }

  const accountInitials = user?.email
    ? user.email
        .split("@")[0]
        .split(/[._-]/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase())
        .join("")
    : user?.fullName
    ? user.fullName
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase())
        .join("")
    : ""

  const handleSearchChange = e => {
    const value = e.target.value
    setSearchTerm(value)

    if (!value) {
      setSearchResults([])
      return
    }

    const results = []

    const searchMenu = items => {
      for (const item of items) {
        if (item.title.toLowerCase().includes(value.toLowerCase())) {
          results.push({ title: item.title, slug: item.slug })
        }
        if (item.children) {
          searchMenu(item.children)
        }
      }
    }

    searchMenu(menuItems)
    setSearchResults(results)

    results.forEach(result => {
      const localizedLink = useLink(result.slug)
      prefetchPathname(localizedLink)
    })
  }

  const handleResultClick = slug => {
    navigate(useLink(slug))
    setSearchTerm("")
    setSearchResults([])
  }

  useEffect(() => {
    const hasDropdown = menuItems.some(
      item => findId(item.title) === openDropdown && item.children?.length > 0
    )

    if (hasDropdown) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth
      document.body.style.overflow = "hidden"
      document.body.style.paddingRight = `${scrollbarWidth}px`
      document.getElementById("main").classList.add("blurredBackground")

      if (window.lenis) {
        window.lenis.stop()
      }
    } else {
      document.body.style.overflow = ""
      document.body.style.paddingRight = ""
      document.getElementById("main").classList.remove("blurredBackground")

      if (window.lenis) {
        window.lenis.start()
      }
    }

    return () => {
      document.body.style.overflow = ""
      document.body.style.paddingRight = ""
      document.getElementById("main").classList.remove("blurredBackground")

      if (window.lenis) {
        window.lenis.start()
      }
    }
  }, [openDropdown, menuItems])

  let timeoutId: NodeJS.Timeout

  useEffect(() => {
    if (openDropdown && megaMenuRef.current) {
      const focusableEls = megaMenuRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const focusable = Array.from(focusableEls)
      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      const currentIndex = menuItems.findIndex(
        item => findId(item.title) === openDropdown
      )
      const currentToggle = document.getElementById(
        `menu-toggle-${currentIndex}`
      )
      const nextToggle = document.getElementById(
        `menu-toggle-${currentIndex + 1}`
      )

      const handleKeyDown = e => {
        if (e.key === "Tab") {
          if (e.shiftKey && e.target === first) {
            e.preventDefault()
            setOpenDropdown(null)
            setTimeout(() => currentToggle?.focus(), 0)
          } else if (!e.shiftKey && e.target === last) {
            setOpenDropdown(null)
            setTimeout(() => nextToggle?.focus(), 0)
          }
        }
      }

      first?.addEventListener("keydown", handleKeyDown)
      last?.addEventListener("keydown", handleKeyDown)

      return () => {
        first?.removeEventListener("keydown", handleKeyDown)
        last?.removeEventListener("keydown", handleKeyDown)
      }
    }
  }, [openDropdown])

  return (
    <nav
      className={styles.desktopNav}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Logo */}
      <div className={styles.logo}>
        <Link to={`${useLink("/")}`} aria-label="Header Logo">
          <img
            src={headerLogo?.file.url}
            height={headerLogo?.height}
            width={headerLogo?.width}
            alt={headerLogo?.title}
            loading="eager"
            className={styles.headerLogo}
            onClick={handleLinkClick}
          />
        </Link>
      </div>
      <div
        className={styles.navigation}
        onMouseEnter={() => {
          clearTimeout(timeoutId)
        }}
        onMouseLeave={() => {
          timeoutId = setTimeout(() => {
            setOpenDropdown("")
            setChildOpenState("")
            setSubChildOpenState("")
          }, 200)
        }}
      >
        <div className={styles.parentWrapper}>
          <ul className={styles.parent}>
            {menuItems.map((menuItem, index) => {
              const id = findId(menuItem.title)
              const isOpen = openDropdown === id
              const newSlug =
                menuItem.slug[0] === "/"
                  ? menuItem.slug.slice(1)
                  : menuItem.slug
              const localizedPath = useLink(`/${newSlug}`)

              return (
                <li
                  key={findId(menuItem.title)}
                  className={`flex ${
                    openDropdown === findId(menuItem.title) ? styles.active : ""
                  }`}
                  onMouseEnter={() => {
                    const id = findId(menuItem.title)

                    if (openDropdown !== id) {
                      const hoveredItem = menuItems.find(
                        item => findId(item.title) === id
                      )
                      const defaultChild = hoveredItem?.children?.length
                        ? findId(hoveredItem.children[0].title)
                        : ""

                      setOpenDropdown(id)
                      setChildOpenState(defaultChild)
                      setSubChildOpenState("")
                    }
                  }}
                >
                  {menuItem.slug ? (
                    <Link to={`${useLink(menuItem.slug)}`}>
                      {menuItem.title}
                    </Link>
                  ) : (
                    <button
                      className={styles.parentLink}
                      id={`menu-toggle-${index}`}
                      aria-label={`${
                        openDropdown === findId(menuItem.title)
                          ? "Collapse"
                          : "Expand"
                      } ${menuItem.title} submenu`}
                      aria-expanded={openDropdown === findId(menuItem.title)}
                      aria-controls={`submenu-${findId(menuItem.title)}`}
                      aria-haspopup="true"
                      onClick={() => {
                        const id = findId(menuItem.title)
                        const isOpening = openDropdown !== id
                        setOpenDropdown(isOpening ? id : null)

                        setTimeout(() => {
                          if (isOpening && megaMenuRef.current) {
                            megaMenuRef.current.focus()
                          }
                        }, 0)
                      }}
                      onKeyDown={e => {
                        const id = findId(menuItem.title)
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          const isOpening = openDropdown !== id
                          setOpenDropdown(isOpening ? id : null)

                          if (isOpening) {
                            const firstChild = menuItem.children?.[0]
                            if (firstChild) {
                              setChildOpenState(findId(firstChild.title))
                            }
                          }

                          setTimeout(() => {
                            if (isOpening && megaMenuRef.current) {
                              megaMenuRef.current.focus()
                            }
                          }, 0)
                        }

                        if (e.key === "Escape") {
                          e.preventDefault()
                          setOpenDropdown(null)
                        }
                      }}
                    >
                      {menuItem.title}
                      {menuItem.children.length > 0 &&
                        (openDropdown === findId(menuItem.title) ? (
                          <span aria-hidden="true">
                            <MdKeyboardArrowUp />
                          </span>
                        ) : (
                          <span aria-hidden="true">
                            <MdKeyboardArrowDown />
                          </span>
                        ))}
                    </button>
                  )}

                  {menuItem.type !== "megamenu" &&
                    openDropdown === findId(menuItem.title) &&
                    menuItem.children.length > 0 && (
                      <div
                        className={styles.defaultDropdown}
                        id={`submenu-${findId(menuItem.title)}`}
                        role="region"
                        aria-label={`${menuItem.title} submenu`}
                      >
                        <ul>
                          {menuItem.children
                            .filter(child => child?.title && child?.slug)
                            .map(child => {
                              const childSlug =
                                child.slug[0] === "/"
                                  ? child.slug.slice(1)
                                  : child.slug
                              const childPath = useLink(`/${childSlug}`)

                              return (
                                <li key={findId(child.title)}>
                                  <div className={styles.menuItem}>
                                    <div className={styles.menuTitle}>
                                      <div className={styles.title}>
                                        <Link
                                          to={childPath}
                                          onClick={handleLinkClick}
                                        >
                                          {child.title}
                                          {child.subTitle && (
                                            <p className={styles.subTitle}>
                                              {child.subTitle}
                                            </p>
                                          )}
                                        </Link>
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              )
                            })}
                        </ul>

                        <div className={styles.quote2}>
                          <p>{headerText}</p>
                          <Link
                            to={`${useLink("/contact-us")}`}
                            aria-label="Contact Us"
                            className={`onPrimaryOutline ${styles.btn}`}
                          >
                            {getAQuoteButton}
                          </Link>
                        </div>
                      </div>
                    )}
                </li>
              )
            })}
          </ul>

          <div className={styles.whatsappImg}>
            <WhatsAppLink
              phoneNumber="919205987106"
              message="Hi, I'm interested in your services."
              loading="eager"
            />
          </div>

          <ul aria-label="Contact Us submenu">
            {headerButton?.map((item, index) => {
              const localizedSlug = useLink(item.slug)
              return (
                <li key={item.id || item.slug || item.title}>
                  <Link
                    to={localizedSlug}
                    className={index === 0 ? "primaryFilled" : ""}
                    title={`Go to ${item.title}`}
                    aria-label={`Go to ${item.title}`}
                  >
                    {item.title}
                  </Link>
                </li>
              )
            })}
          </ul>

          <div className={styles.accountLinks}>
            {/* Logged-in avatar — always in DOM so CSS can show it during SSR */}
            <div
              className={styles.loggedInGroup}
              data-saas-auth="logged-in"
              style={!user ? { display: 'none' } : undefined}
            >
              <div
                className={styles.profileCapsuleContainer}
                ref={profileDropdownRef}
              >
                <div
                  className={styles.profileCapsule}
                  onClick={() => setProfileDropdownOpen(prev => !prev)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      setProfileDropdownOpen(prev => !prev)
                    }
                  }}
                  aria-haspopup="true"
                  aria-expanded={profileDropdownOpen}
                >
                  <div className={styles.avatarCircle}>{accountInitials}</div>
                  <MdKeyboardArrowDown
                    className={`${styles.profileChevron} ${
                      profileDropdownOpen ? styles.open : ""
                    }`}
                  />
                </div>
                {profileDropdownOpen && (
                  <div className={styles.profileDropdown} role="menu">
                    <Link
                      to={useLink("dashboard")}
                      className={styles.dropdownItem}
                      role="menuitem"
                      onClick={e => {
                        e.stopPropagation()
                        setProfileDropdownOpen(false)
                      }}
                    >
                      <LayoutDashboard size={16} />
                      My Dashboard
                    </Link>
                    <button
                      type="button"
                      className={styles.dropdownItem}
                      onClick={e => {
                        e.stopPropagation()
                        setProfileDropdownOpen(false)
                        logout()
                      }}
                      role="menuitem"
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
            {/* Login button — always in DOM so CSS can hide it during SSR */}
            <div
              className={styles.loggedOutGroup}
              data-saas-auth="logged-out"
              style={user ? { display: 'none' } : undefined}
            >
              <button
                type="button"
                className={styles.loginBtn}
                title={loginText || "Log in"}
                aria-label={loginText || "Log in"}
                onClick={() => openAuthModal("login", dashboardRedirectPath)}
              >
                {loginText || "Log in"}
              </button>
            </div>
          </div>
        </div>
        {menuItems.map(
          menuItem =>
            menuItem.type === "megamenu" &&
            openDropdown === findId(menuItem.title) &&
            menuItems.find(item => findId(item.title) === openDropdown)
              ?.children.length > 0 && (
              <div
                ref={megaMenuRef}
                tabIndex={-1}
                className={styles.megaMenu}
                id={`submenu-${findId(menuItem.title)}`}
                role="region"
                aria-label={`${menuItem.title} submenu`}
              >
                <div
                  className={`container global-spacing ${styles.megaMenuHeader}`}
                >
                  <p className={styles.categoryTitle}>
                    {menuItem.title?.trim().toLowerCase() === "resource"
                      ? allResources
                      : menuItem.title?.trim().toLowerCase() === "services"
                      ? allServices
                      : menuItem.title}
                  </p>

                  <div className={styles.searchWrapper}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="25"
                      viewBox="0 0 24 25"
                      fill="none"
                      className={styles.searchIcon}
                    >
                      <mask
                        id="mask0_7749_1843"
                        style={{ maskType: "alpha" }}
                        maskUnits="userSpaceOnUse"
                        x="0"
                        y="0"
                        width="24"
                        height="25"
                      >
                        <rect y="0.5" width="24" height="24" fill="#D9D9D9" />
                      </mask>
                      <g mask="url(#mask0_7749_1843)">
                        <path
                          d="M19.6 21.5L13.3 15.2C12.8 15.6 12.225 15.9167 11.575 16.15C10.925 16.3833 10.2333 16.5 9.5 16.5C7.68333 16.5 6.14583 15.8708 4.8875 14.6125C3.62917 13.3542 3 11.8167 3 10C3 8.18333 3.62917 6.64583 4.8875 5.3875C6.14583 4.12917 7.68333 3.5 9.5 3.5C11.3167 3.5 12.8542 4.12917 14.1125 5.3875C15.3708 6.64583 16 8.18333 16 10C16 10.7333 15.8833 11.425 15.65 12.075C15.4167 12.725 15.1 13.3 14.7 13.8L21 20.1L19.6 21.5ZM9.5 14.5C10.75 14.5 11.8125 14.0625 12.6875 13.1875C13.5625 12.3125 14 11.25 14 10C14 8.75 13.5625 7.6875 12.6875 6.8125C11.8125 5.9375 10.75 5.5 9.5 5.5C8.25 5.5 7.1875 5.9375 6.3125 6.8125C5.4375 7.6875 5 8.75 5 10C5 11.25 5.4375 12.3125 6.3125 13.1875C7.1875 14.0625 8.25 14.5 9.5 14.5Z"
                          fill="#97A3B7"
                        />
                      </g>
                    </svg>
                    <input
                      type="text"
                      placeholder={search}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onKeyDown={e => {
                        if (e.key === "Enter" && searchResults.length > 0) {
                          const firstResult = searchResults[0]
                          const localizedLink = useLink(firstResult.slug)
                          setSearchTerm("")
                          setSearchResults([])
                          setOpenDropdown("")
                          setTimeout(() => navigate(localizedLink), 0)
                        }
                      }}
                      className={styles.searchInput}
                    />

                    {searchResults.length > 0 && (
                      <ul className={styles.searchResults}>
                        {searchResults.map((result, idx) => {
                          const localizedLink = useLink(result.slug)
                          return (
                            <li key={idx}>
                              <Link
                                to={localizedLink}
                                onClick={() => {
                                  setSearchTerm("")
                                  setSearchResults([])
                                  setOpenDropdown("")
                                }}
                              >
                                {result.title}
                              </Link>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                </div>
                <div className={styles.advImgWrapper}>
                  <div className={styles.menuContainer}>
                    {/* LEVEL 1 */}
                    <div className={styles.level1}>
                      <ul>
                        {menuItems
                          .find(item => findId(item.title) === openDropdown)
                          ?.children.filter(item => item?.title && item?.slug)
                          .map(item => (
                            <li
                              key={findId(item.title)}
                              className={
                                childOpenState === findId(item.title)
                                  ? styles.active
                                  : ""
                              }
                              onMouseEnter={() => {
                                setChildOpenState(findId(item.title))
                                setSubChildOpenState("")
                              }}
                              onKeyUp={e => {
                                setChildOpenState(findId(item.title))
                                setSubChildOpenState("")
                              }}
                            >
                              <div className={styles.menuItem}>
                                <div className={styles.menuTitle}>
                                  <div>
                                    <Link
                                      to={useLink(item.slug)}
                                      onClick={handleLinkClick}
                                    >
                                      {item.title}
                                    </Link>
                                    {item.subTitle && (
                                      <p className={styles.subTitle}>
                                        {item.subTitle}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </li>
                          ))}
                      </ul>
                    </div>

                    {/* LEVEL 2 + LEVEL 3 */}
                    <div className={styles.level2}>
                      <ul>
                        <li>
                          {menuItems
                            .find(item => findId(item.title) === openDropdown)
                            ?.children.find(
                              child => findId(child.title) === childOpenState
                            )
                            ?.children?.map((child, idx) => {
                              const childId = findId(child.title)
                              return (
                                <div
                                  key={idx}
                                  className={`${styles.menuItem} ${
                                    subChildOpenState === childId
                                      ? styles.active
                                      : ""
                                  }`}
                                  onMouseEnter={() =>
                                    setSubChildOpenState(childId)
                                  }
                                >
                                  <div className={styles.menuTitle}>
                                    <div>
                                      <Link
                                        to={useLink(child.slug)}
                                        onClick={handleLinkClick}
                                      >
                                        {child.title}
                                      </Link>
                                      <p className={styles.subTitle}>
                                        {child.subTitle}
                                      </p>
                                    </div>
                                  </div>

                                  {child.children?.length > 0 && (
                                    <div className={styles.level3Content}>
                                      <div className={styles.level3Tags}>
                                        {child.children.map((subChild, i) => {
                                          if (
                                            !subChild?.slug ||
                                            !subChild?.title
                                          )
                                            return null

                                          return (
                                            <Link
                                              key={i}
                                              to={useLink(subChild.slug)}
                                              onClick={handleLinkClick}
                                              className={styles.tagButton}
                                            >
                                              {subChild.title}
                                            </Link>
                                          )
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                        </li>
                      </ul>
                    </div>

                    <div className={styles.level3Box}>
                      <HeaderImageSlider data={headerBlogMedia} />
                    </div>
                  </div>

                  <div className={`flex justify-between ${styles.quote}`}>
                    <p>{quoteSectionTitle}</p>
                    <button>
                      {quoteSection.map((item, index) => (
                        <li key={item.id || item.slug || item.title}>
                          <Link
                            to={useLink(item.slug)}
                            className={
                              index === 0 ? `${styles.primaryFilledNew}` : ""
                            }
                            title={`Go to ${item.title}`}
                            aria-label={`Go to ${item.title}`}
                            onClick={handleLinkClick}
                          >
                            {item.title}
                          </Link>
                        </li>
                      ))}
                    </button>
                  </div>
                </div>
              </div>
            )
        )}
      </div>
    </nav>
  )
}

export default DesktopMenu
